import React, { useEffect, useMemo, useState } from "react";
import {
    bidUpdateApplyEventHandler,
    getActiveModule,
    internationalizeNumericValue,
    parseInternationalizedValue,
    cellSpinnerHandler,
} from "@ts/utils";
import { showToast } from "@ts/modals";
import * as Sentry from "@sentry/react";
import $ from "jquery";
import {
    Actions,
    CampaignParams,
    Channels,
    DeepDiveGridType,
    IChannel,
    ICustomCellRendererParams,
    Modules,
    ParentModule,
    ProgramType,
    SubModule,
    TBudgetUpdateModules,
    TClientTimezone,
    UtilsModule,
    ActionType,
    CAMPAIGN_SYNC_STATE,
} from "@ts/common-types";
import { useCampaignSyncCheck, useGetModulesData, usePermissionCheck } from "@hooks/AllCustomHooks";
import { useDispatch, useSelector } from "react-redux";
import CampaignDurationCellRenderer from "./CampaignDurationCellRenderer";
import { FaCheck, FaTrashAlt } from "react-icons/fa";
import { FaCirclePlus } from "react-icons/fa6";
import {
    updateCpmModalDisplay,
    updateDailyBudgetModalData,
    updateDailyBudgetModalDisplay,
} from "@features/modalSlice";
import { Popover } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
    DATE_BUDGET_UPDATE_CONFIG,
    DISABLED_PLACEMENTS,
    DISABLED_PROGRAM_TYPES,
    MODULES_REQ_KEYWORDS,
    SKU_DEPENDENT_MODULES,
} from "@/ts/constants";
import { setUpdateGridData } from "@ts/filters";
import { IoMdLink } from "react-icons/io";
import { includes } from "underscore";


interface IUpdateBidOrBudgetCellRendererParams extends ICustomCellRendererParams {
    isCampaignSyncStatusRequired?: boolean;
}

const UpdateBidOrBudgetCellRenderer = (
    params: IUpdateBidOrBudgetCellRendererParams,
    contextType?: string
) => {
    const dispatch = useDispatch();
    const channel = useSelector((state: IChannel) => state.channel.value);
    const { activeModule } = getActiveModule();
    const { isModuleEditDisabled } = usePermissionCheck({
        type: "cellRenderer",
    });

    const isDeepDiveEnabled = useMemo(
        () => includes(SKU_DEPENDENT_MODULES, activeModule),
        [activeModule]
    );

    const { isCampaignSyncCheckRequired } = useCampaignSyncCheck({params});

    const moduleParams = useMemo(() => {
        if (!isDeepDiveEnabled) return {};

        const moduleMap: Partial<
            Record<
                DeepDiveGridType,
                {
                    activeModuleName: Modules;
                    activeParentModuleName: ParentModule;
                }
            >
        > = {
            [DeepDiveGridType.Campaign]: {
                activeModuleName: SubModule.CAMPAIGN,
                activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
            },
            [DeepDiveGridType.Keyword]: {
                activeModuleName: SubModule.KEYWORD,
                activeParentModuleName: ParentModule.PRODUCT_ANALYTICS,
            },
        };

        return moduleMap[contextType as DeepDiveGridType] || {};
    }, [contextType, isDeepDiveEnabled]);

    const actions = useGetModulesData({
        type: "actions",
        ...moduleParams,
    });

    const deepDiveGridType: string | null = useMemo(
        () =>
            includes(SKU_DEPENDENT_MODULES, activeModule)
                ? DeepDiveGridType.Campaign
                : null,
        [activeModule]
    );
    const {
        identifier,
        previous_value,
        requiredDailyBudget,
        dailyBudgetModalBtnId,
        dailyBudgetDisabledTill,
    } = useMemo(() => {
        return {
            identifier:
                deepDiveGridType !== DeepDiveGridType.Campaign &&
                includes(MODULES_REQ_KEYWORDS, activeModule)
                    ? params.data.sys_ams_keyword_id
                    : params.data.sys_campaign_id,
            previous_value:
                deepDiveGridType !== DeepDiveGridType.Campaign &&
                includes(MODULES_REQ_KEYWORDS, activeModule)
                    ? params.data.bid
                    : params.data.budget,
            requiredDailyBudget: +params.data.daily_budget,
            dailyBudgetModalBtnId: `dailyBudgetModalBtn_${params.data.sys_campaign_id}`,
            dailyBudgetDisabledTill: params.data?.daily_budget_disabled_till,
        };
    }, [deepDiveGridType, activeModule, params.data]);
    const clientTimezone = useSelector(
        (state: TClientTimezone) => state.clientTimezone.value
    );

    const [inputValue, setInputValue] = useState<string | number>(
        internationalizeNumericValue(params.data[params.column.colId]) ?? ""
    );
    const [applyBtnStatus, setApplyBtnStatus] = useState<boolean>(false);
    const [cancelBtnStatus, setCancelBtnStatus] = useState<boolean>(false);
    const [tillBudgetEnds, setTillBudgetEnds] = useState<boolean>(
        params.data.till_budget_ends || false
    );
    const [equallyDivideBudget, setEquallyDivideBudget] = useState(
        params.data.equally_divide_budget || false
    );
    const [duration, setDuration] = useState<Dayjs>(params.data?.end_date);
    const [programType] = useState<ProgramType>(params.data.program_type);
    const otherAttributes = {
        baseProgramType: programType,
        costType: params.data.cost_type || undefined,
        campaignBudget: params.data.campaign_budget,
        minBidValue: params.data.min_bid_value,
        maxBidValue: params.data.max_bid_value,
    };
    const [bidLimitMessage, setBidLimitMessage] = useState<string>("");
    const [isUIDisabled, setIsUIDisabled] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let disabledState = isModuleEditDisabled ||
            params.data.is_archived_entity ||
            (activeModule !== SubModule.CAMPAIGN &&
                (DISABLED_PROGRAM_TYPES.includes(
                    params.data.program_type
                ) ||
                    gridParams?.excludedBidProgramType?.includes(
                        params.data.program_type
                    ))) || (params.data.is_keywords_added && DISABLED_PLACEMENTS.includes(params.data.keyword_name));

        if (isCampaignSyncCheckRequired) {
            const isPending = params.data.sync_state === CAMPAIGN_SYNC_STATE.PENDING;
            disabledState = isPending || !!disabledState;
        }

        setIsUIDisabled(disabledState);
    }, [isModuleEditDisabled, isCampaignSyncCheckRequired, activeModule, params]);

    const toggleChangeLock = function () {
        setApplyBtnStatus(!applyBtnStatus);
        setCancelBtnStatus(!cancelBtnStatus);
    };

    const gridParams = useGetModulesData({
        type: "grid-params",
        ...(deepDiveGridType === DeepDiveGridType.Campaign
            ? {
                  activeModuleName: SubModule.CAMPAIGN,
                  activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
              }
            : {}),
    });

    const componentConfig =
        useGetModulesData({
            type: "component-config",
            ...(deepDiveGridType === DeepDiveGridType.Campaign
                ? {
                      activeModuleName: SubModule.CAMPAIGN,
                      activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
                  }
                : {}),
        }) || {};

    async function applyChange(isCancel: boolean) {
        const parsedInputValue = +parseInternationalizedValue(inputValue);
        const campaignDurationDiv = $(
            `#campaign-duration-container-${identifier}`
        );
        toggleChangeLock();
        try {
            let response;
            if (
                deepDiveGridType !== DeepDiveGridType.Campaign &&
                includes(MODULES_REQ_KEYWORDS, activeModule)
            ) {
                response = await bidUpdateApplyEventHandler(
                    isCancel ? previous_value : parsedInputValue,
                    identifier,
                    otherAttributes
                );

                if (!response.status) {
                    throw new Error(response.message);
                }

                showToast("success", response.message);
                !params?.isModalComponent &&
                    setUpdateGridData({
                        params,
                        value: parseInternationalizedValue(inputValue),
                        isReduxState: true,
                    });
            } else {
                const { updateFunc, updateRecordId, updateRecordKey } =
                    DATE_BUDGET_UPDATE_CONFIG[
                        activeModule as TBudgetUpdateModules
                    ];

                const budgetType = params.data.budget_type;
                const hasBudgetType =
                    budgetType !== undefined && budgetType !== null;

                let isBudgetReduceAllowed = false;

                if (hasBudgetType) {
                    // Campaign-level check: Allow reduction only for daily budgets
                    isBudgetReduceAllowed =
                        budgetType.toString().toUpperCase() === "DAILY";
                } else {
                    // Fallback to account-level flag for channels without budget_type (e.g., Flipkart, Myntra)
                    isBudgetReduceAllowed =
                        gridParams?.isBudgetReduceAllowed ?? true;
                }

                if (
                    !isBudgetReduceAllowed &&
                    parsedInputValue < previous_value
                ) {
                    const errorMessage = hasBudgetType
                        ? "Campaign budget cannot be reduced for lifetime budget campaigns"
                        : "Campaign budget cannot be reduced in non draft campaigns";
                    showToast("danger", errorMessage);
                    setInputValue(internationalizeNumericValue(previous_value));
                    toggleChangeLock();
                    return;
                } else if (
                    activeModule === SubModule.ADGROUP &&
                    parsedInputValue > params.data.campaign_budget
                ) {
                    showToast(
                        "danger",
                        "Adgroup budget cannot be greater than its campaign's budget"
                    );
                    setInputValue(internationalizeNumericValue(previous_value));
                    toggleChangeLock();
                    return;
                } else if (
                    !componentConfig.end_date.disabledTypes?.includes(
                        params.data.program_type
                    ) &&
                    channel !== Channels.AMAZON &&
                    !duration &&
                    !tillBudgetEnds
                ) {
                    showToast(
                        "danger",
                        "Either end date or till budget ends should be specified"
                    );
                    toggleChangeLock();
                    return;
                } else {
                    const additionalParams: CampaignParams =
                        gridParams?.isDateChangeAllowed &&
                        !componentConfig.end_date.disabledTypes?.includes(
                            params.data.program_type
                        )
                            ? {
                                  endDate: duration
                                      ? dayjs
                                            .tz(
                                                dayjs(
                                                    typeof duration === "string"
                                                        ? dayjs(
                                                              duration,
                                                              "YYYY-MM-DD"
                                                          )
                                                        : duration
                                                ).utc(),
                                                clientTimezone.value
                                            )
                                            .endOf("day")
                                            .valueOf()
                                      : 0,
                                  tillBudgetEnds:
                                      campaignDurationDiv.attr(
                                          "data-tillBudgetEnds"
                                      ) === "true",
                                  ...(componentConfig["equally_divide_budget"]
                                      ?.payloadKey && {
                                      [componentConfig["equally_divide_budget"]
                                          ?.payloadKey]: equallyDivideBudget,
                                  }),
                              }
                            : {};

                    const updateRecords = [
                        {
                            [updateRecordKey]:
                                params.data[updateRecordId].toString(),
                            changeType: ActionType.ABSOLUTE,
                            changeValue: isCancel
                                ? previous_value
                                : parsedInputValue,
                            attribute: ["budget"],
                            state: params.data.state || "enabled",
                            ...additionalParams,
                        },
                    ];

                    const updateMethod = (
                        (await import("../../ts/utils")) as UtilsModule
                    )[updateFunc];
                    response = await updateMethod(updateRecords);
                    if (!response.status) {
                        throw new Error(response.message);
                    }
                    showToast("success", response.message);
                    setUpdateGridData({
                        params,
                        value: parseInternationalizedValue(inputValue),
                        ...(Object.keys(additionalParams).length && {
                            additionalValuesToUpdate: {
                                till_budget_ends:
                                    additionalParams?.tillBudgetEnds,
                                equally_divide_budget:
                                    additionalParams[
                                        componentConfig["equally_divide_budget"]
                                            ?.payloadKey as keyof CampaignParams
                                    ],
                                end_date: duration
                                    ? new Date(
                                          additionalParams.endDate
                                      ).toISOString()
                                    : additionalParams?.tillBudgetEnds
                                    ? null
                                    : dayjs().toISOString(),
                            },
                        }),
                        isReduxState: true,
                    });
                }
            }
        } catch (err) {
            $(`#budget-update-field-${identifier}`).val(previous_value);
            setInputValue(previous_value);
            setEquallyDivideBudget(!equallyDivideBudget);
            const resErrMsg = (err as Error).message;
            const errorMessage = includes(MODULES_REQ_KEYWORDS, activeModule)
                ? `Error While Updating Bid for ${params.data.keyword_name}`
                : ` Error While Updating Budget for ${
                      params.data[
                          DATE_BUDGET_UPDATE_CONFIG[
                              activeModule as TBudgetUpdateModules
                          ].recordNameKey
                      ]
                  }`;
            showToast(
                "danger",
                `${errorMessage} ${resErrMsg && ": " + resErrMsg}`
            );
            Sentry.captureException(err);
        }
        toggleChangeLock();
    }

    const cancelBtnHandler = function () {
        $(`#budget-update-field-${identifier}`).val(previous_value);
        setInputValue(previous_value);
        setBidLimitMessage("");
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setEquallyDivideBudget(e.target.checked);

    const handleBidBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const minAllowedBudget = gridParams.budget?.minAllowedBudget;
        if (params?.colDef.field === "budget" && !minAllowedBudget) {
            return;
        }
        const changedBid = e.target.value;
        const { min_bid_value: minBidLimit, max_bid_value: maxBidLimit } =
            params.data;
        if (minBidLimit && changedBid < minBidLimit)
            setBidLimitMessage(`Min permissible CPC is ${minBidLimit}`);
        else if (maxBidLimit && changedBid > maxBidLimit)
            setBidLimitMessage(`Max permissible CPC is ${maxBidLimit}`);
        else if (minAllowedBudget && +changedBid < minAllowedBudget) {
            setBidLimitMessage(`Min permissible Budget is ${minAllowedBudget}`);
        } else setBidLimitMessage("");
    };

    const { condition, isRequiredBudgetAvailable } = useMemo(
        () => ({
            isRequiredBudgetAvailable: !!actions.includes(
                Actions.REQUIRED_DAILY_BUDGET_CHANGE
            ),
            condition:
                inputValue === undefined ||
                inputValue === null ||
                ![Actions.BUDGET_CHANGE, Actions.BID_CHANGE].some((action) =>
                    actions.includes(action)
                ),
        }),
        [actions]
    );

    const getIconInfo = () =>
        requiredDailyBudget
            ? dayjs(dayjs()).isBefore(dailyBudgetDisabledTill) ||
              dayjs(dayjs()).isSame(dailyBudgetDisabledTill, "day")
                ? {
                      color: "#FFD95A",
                      text: `Daily budget disabled till ${dayjs(
                          dailyBudgetDisabledTill
                      ).format("YYYY-MM-DD")}`,
                  }
                : {
                      color: "#5cb85c",
                      text: `Daily budget set to ${requiredDailyBudget}`,
                  }
            : { color: "#B2B2B2" };

    const dailyBudgetModalButton = () => {
        return (
            <button
                className="btn btn-link"
                type="button"
                onClick={() => {
                    dispatch(updateDailyBudgetModalData(params.data));
                    dispatch(updateDailyBudgetModalDisplay(true));
                }}
                style={{
                    cursor: "pointer",
                    pointerEvents: isUIDisabled ? "none" : "unset",
                }}
            >
                <FaCirclePlus
                    id={dailyBudgetModalBtnId}
                    style={{
                        fontSize: "16px",
                        color: getIconInfo().color,
                    }}
                />
            </button>
        );
    };

    return (
        <>
            {params.node.rowPinned ? null : (
                <div
                    id={`BudgetUpdateBtn-${identifier}`}
                    className="ag-cell-wrapper"
                    style={
                        condition
                            ? { pointerEvents: "none", opacity: "0.6" }
                            : null
                    }
                >
                    {!!gridParams?.isDateChangeAllowed && (
                        <CampaignDurationCellRenderer
                            {...{
                                params,
                                tillBudgetEnds,
                                setTillBudgetEnds,
                                duration,
                                setDuration,
                                identifier,
                                setEquallyDivideBudget,
                                isUIDisabled,
                                componentConfig,
                                gridParams,
                            }}
                        />
                    )}

                    <div style={{ display: "grid" }}>
                        <div
                            className={`input-group input-group-sm ${
                                gridParams.isCpmChangeAllowed ||
                                componentConfig["equally_divide_budget"]
                                    ?.isVisible
                                    ? `mt-${
                                          gridParams.cpmChangeAllowedTypes?.includes(
                                              programType
                                          )
                                              ? "2"
                                              : "1"
                                      }`
                                    : ""
                            }`}
                            style={{
                                width: gridParams?.isDateChangeAllowed
                                    ? "11rem"
                                    : "8.5rem",
                            }}
                        >
                            <input
                                type="text"
                                name="bid-input"
                                value={inputValue}
                                id={`budget-update-field-${identifier}`}
                                className="form-control border border-primary"
                                data-id={identifier}
                                data-value={parseInternationalizedValue(
                                    inputValue
                                )}
                                disabled={isUIDisabled}
                                onFocus={() => {
                                    setInputValue(
                                        parseInternationalizedValue(
                                            inputValue
                                                ? inputValue
                                                : previous_value
                                        )
                                    );
                                }}
                                onBlur={() => {
                                    setInputValue(
                                        internationalizeNumericValue(
                                            inputValue
                                                ? inputValue
                                                : previous_value
                                        )
                                    );
                                }}
                                onChange={(e) => {
                                    if (
                                        includes(
                                            MODULES_REQ_KEYWORDS,
                                            activeModule
                                        ) ||
                                        /^\d*\.?\d{0,2}$/.test(e.target.value)
                                    ) {
                                        setInputValue(
                                            e.target.value ? e.target.value : ""
                                        );

                                        channel !== Channels.AMAZON &&
                                            handleBidBudgetChange(e);
                                    }
                                }}
                                style={
                                    params.data.budget === null
                                        ? {
                                              backgroundColor:
                                                  "rgb(235, 235, 228,0.4)",
                                          }
                                        : null
                                }
                            />
                            <button
                                className="btn btn-light px-2 border-start border-top-0 border-bottom-0 border-primary"
                                value="Apply"
                                type="button"
                                disabled={loading || isUIDisabled}
                                onClick={() => {
                                    if (
                                        inputValue ===
                                            internationalizeNumericValue(
                                                previous_value
                                            ) &&
                                        equallyDivideBudget ===
                                            (params.data
                                                .equally_divide_budget ||
                                                false) &&
                                        tillBudgetEnds ===
                                            (params.data.till_budget_ends ||
                                                false) &&
                                        duration ===
                                            (params.data.end_date || "")
                                    ) {
                                        showToast(
                                            "danger",
                                            "Please update the value."
                                        );
                                        return;
                                    }
                                    cellSpinnerHandler(setLoading, applyChange);
                                }}
                            >
                                {loading ? (
                                    <i
                                        className="spinner-border text-primary"
                                        style={{ width: "1em", height: "1em" }}
                                    />
                                ) : (
                                    <FaCheck
                                        id={`${identifier}-applyBtnId`}
                                        data-id="Apply"
                                        className="text-primary"
                                        style={
                                            bidLimitMessage
                                                ? {
                                                      pointerEvents: "none",
                                                      opacity: "0.6",
                                                  }
                                                : null
                                        }
                                    />
                                )}
                            </button>
                            <button
                                className="btn btn-light px-2"
                                type="button"
                                disabled={isUIDisabled}
                            >
                                <FaTrashAlt
                                    id={`${identifier}-cancelBtnId`}
                                    data-id="Cancel"
                                    onClick={cancelBtnHandler}
                                />
                            </button>
                            {isRequiredBudgetAvailable &&
                                (activeModule === SubModule.CAMPAIGN ||
                                    deepDiveGridType ===
                                        DeepDiveGridType.Campaign) &&
                                (params.data.daily_budget ? (
                                    <Popover
                                        styles={{ body: { padding: "10px" } }}
                                        placement="right"
                                        content={
                                            <strong>
                                                {getIconInfo().text}
                                            </strong>
                                        }
                                    >
                                        {dailyBudgetModalButton()}
                                    </Popover>
                                ) : (
                                    dailyBudgetModalButton()
                                ))}
                        </div>
                        {componentConfig["equally_divide_budget"]?.isVisible &&
                            componentConfig[
                                "equally_divide_budget"
                            ]?.showForTypes.includes(programType) && (
                                <div
                                    className="d-flex mt-1 align-items-center"
                                    style={{
                                        lineHeight: "1.5",
                                        fontSize: "10px",
                                    }}
                                    id={`${identifier}-${componentConfig["equally_divide_budget"]?.label}`}
                                >
                                    <input
                                        className="me-1"
                                        type="checkbox"
                                        name={`${componentConfig["equally_divide_budget"]?.label}`}
                                        id={`${identifier}-${componentConfig["equally_divide_budget"]?.label}-check`}
                                        onChange={handleCheckboxChange}
                                        checked={equallyDivideBudget}
                                        disabled={
                                            isUIDisabled || tillBudgetEnds
                                        }
                                    />
                                    <label
                                        className="me-1"
                                        htmlFor="budget-duration"
                                    >
                                        {
                                            componentConfig[
                                                "equally_divide_budget"
                                            ]?.label
                                        }
                                    </label>
                                </div>
                            )}
                        {channel !== Channels.AMAZON && (
                            <div
                                className="d-flex mt-1 align-items-center text-danger"
                                style={{
                                    lineHeight: "1.5",
                                    fontSize: "10px",
                                }}
                                id={`${identifier}-bid-limit-span`}
                            >
                                <span className="me-1">{bidLimitMessage}</span>
                            </div>
                        )}
                        {gridParams.isCpmChangeAllowed &&
                            gridParams.cpmChangeAllowedTypes.includes(
                                programType
                            ) &&
                            channel !== Channels.ZEPTO && (
                                <div
                                    className="d-flex justify-content-end"
                                    style={{
                                        lineHeight: "1.2",
                                        width: "7.3rem",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        dispatch(
                                            updateDailyBudgetModalData(
                                                params.data
                                            )
                                        );
                                        dispatch(updateCpmModalDisplay(true));
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: 10,
                                            marginTop: 3,
                                            color: "blue",
                                        }}
                                        id={`${identifier}-budget-duration-check`}
                                    >
                                        {`${componentConfig["cpm"].label}: ${params.data.cpm_bid}`}
                                    </span>
                                    <IoMdLink
                                        className="mx-1"
                                        style={{
                                            fontSize: "15px",
                                            color: "grey",
                                            marginTop: 1,
                                        }}
                                    />
                                </div>
                            )}
                    </div>
                </div>
            )}
        </>
    );
};

export default UpdateBidOrBudgetCellRenderer;
